import React from 'react';
import { Helmet } from 'react-helmet-async';

export const SITE_URL = 'https://www.etenant.net';

/** Default social share image (1200x630, made from etenant-banner-10232026-v1.png) */
const DEFAULT_IMAGE = `${SITE_URL}/og-image-v2.jpg`;
const DEFAULT_IMAGE_ALT = 'eTENANT app — manage homes, apartments, condos, commercial spaces, and parking slots in one place';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  imageAlt?: string;
  path?: string;
  type?: string;
  noindex?: boolean;
  includeAppSchema?: boolean;
  /** Adds a BreadcrumbList (Home > this page) for inner pages */
  breadcrumb?: string;
  /** Adds FAQPage structured data */
  faqs?: { question: string; answer: string }[];
}

const appSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      'url': SITE_URL,
      'name': 'eTENANT',
      'publisher': { '@id': `${SITE_URL}/#organization` },
      'inLanguage': 'en'
    },
    {
      '@type': 'MobileApplication',
      'name': 'eTENANT',
      'description': 'A rental management app for landlords and property owners. Manage properties, units, tenants, rental agreements, rent payments, recurring charges, maintenance requests, inventory, and documents from your phone.',
      'url': SITE_URL,
      'image': DEFAULT_IMAGE,
      'applicationCategory': 'BusinessApplication',
      'operatingSystem': 'Android',
      'softwareVersion': '1.3.68',
      'author': { '@id': `${SITE_URL}/#organization` },
      'offers': {
        '@type': 'Offer',
        'price': '0',
        'priceCurrency': 'USD'
      },
      'installUrl': 'https://play.google.com/store/apps/details?id=net.etenant.app',
      'downloadUrl': 'https://play.google.com/store/apps/details?id=net.etenant.app',
      'featureList': [
        'Smart Dashboard',
        'Property Management',
        'Unit Management',
        'Property Inventory',
        'Tenant Management',
        'Occupant Records',
        'Rental Agreements',
        'AI Agreement Drafts',
        'Tenant Access Link',
        'Document Storage',
        'Payment Tracking',
        'PDF Receipts with QR Codes',
        'Recurring Charges',
        'Maintenance Requests',
        'Smart Notifications',
        'Rent Collection Reports'
      ]
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      'name': 'INVOVANE COMPANY',
      'url': 'https://invovane.com',
      'logo': `${SITE_URL}/logo512.png`,
      'sameAs': ['https://web.facebook.com/etenant.net'],
      'contactPoint': {
        '@type': 'ContactPoint',
        'contactType': 'customer support',
        'email': 'support@etenant.net'
      }
    }
  ]
};

const SEO: React.FC<SEOProps> = ({
  title = 'eTENANT: Rental Manager App for Landlords',
  description = 'eTENANT is a free Android app for landlords and property owners. Manage properties, tenants, and rent, track maintenance, and draft agreements with AI.',
  keywords = 'rental management app, property management app, landlord app, rent tracker, tenant management, rental agreement, lease agreement, AI lease drafting, rent collection, maintenance requests, property inventory, eTENANT',
  image = DEFAULT_IMAGE,
  imageAlt = DEFAULT_IMAGE_ALT,
  path = '/',
  type = 'website',
  noindex = false,
  includeAppSchema = false,
  breadcrumb,
  faqs
}) => {
  const fullTitle = title.includes('eTENANT') ? title : `${title} | eTENANT`;
  const url = `${SITE_URL}${path}`;

  const breadcrumbSchema = breadcrumb && {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': `${SITE_URL}/` },
      { '@type': 'ListItem', 'position': 2, 'name': breadcrumb, 'item': url }
    ]
  };

  const faqSchema = faqs && faqs.length > 0 && {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': { '@type': 'Answer', 'text': faq.answer }
    }))
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="INVOVANE COMPANY" />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large, max-snippet:-1'}
      />

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="eTENANT" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      {image === DEFAULT_IMAGE && <meta property="og:image:width" content="1200" />}
      {image === DEFAULT_IMAGE && <meta property="og:image:height" content="630" />}

      {/* Twitter Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {!noindex && <link rel="canonical" href={url} />}

      {/* Structured Data */}
      {includeAppSchema && (
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      )}
      {faqSchema && (
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      )}
    </Helmet>
  );
};

export default SEO;
